import React from 'react';
import PropTypes from 'prop-types';
import { Avatar, H4, Icon } from '@contently/toolkit';
import styled from '@emotion/styled';
import GA from '../utils/GoogleAnalytics';

const ShareIcon = styled(Icon)`
  position: absolute;
  top: 1em;
  right: 1em;
  font-size: 0.75rem;
`;

const ContributorAvatar = styled(Avatar)`
  height: 80px;
  width: 80px;
  margin-left: auto;
  margin-right: auto;
  background: #e5e5e5;
  i {
    background: none;
    &:before {
      width: 80px;
      font-size: 32px;
    }
  }
`;

const Name = styled(H4)`
  margin-top: 15px;
  text-align: center;
  font-size: 1rem;
`;

const StyledBio = styled('p')`
  margin: 0;
  font-size: 0.75rem;
  font-weight: 300;
  line-height: 1.4;
  max-height: 134px;
  overflow: hidden;
`;

const blurStyles = `
  filter: blur(3px);
  overflow: hidden;
`;

const CardInnerWrapper = styled('div')`
  width: 100%;
  box-sizing: border-box;
  padding: 1.625em 1.5em;
  background: white;
  border-top: 0.375rem solid #0094ab;
  box-shadow: 0 2px 6px 0 #f6f6f6, inset 0 0 1px 0 #e5e5e5;
  transition: box-shadow 0.5s ease-in-out;
  position: relative;
  border-radius: 0 0 3px 3px;
  margin-right: 1.625em;
`;

const Card = styled('div')`
  width: 20%;
  height: 20em;
  margin-bottom: 2em;
  display: flex;
  justify-content: stretch;
  align-content: stretch;
  box-sizing: border-box;
  ${props => (props.blur ? `${blurStyles}` : '')};
  ${props => (!props.blur ? `cursor: pointer;` : '')};
  &:hover {
    > div {
      ${props => (!props.blur ? `box-shadow: 0 5px 15px rgba(0,0,0,0.3);` : '')}
    }
  }
  @media screen and (max-width: 1400px) {
    width: 25%;
  }
  @media screen and (max-width: 1150px) {
    width: 33.333333%;
  }
  @media screen and (max-width: 780px) {
    width: 48%;
    margin-right: 2%;
    height: auto;
    &:nth-of-type(even) {
      margin-right: 0;
    }
  }
  @media screen and (max-width: 600px) {
    margin: 0 0 1.625em;
    width: 100%;
    height: auto;
  }
`;

const truncateBio = bio => {
  return bio.length > 190 ? `${bio.substr(0, 189).trim()}...` : bio;
};

const Contributor = props => {
  const { contributor, blur } = props;
  const { name, avatarUrl, bio, portfolioUrl } = contributor;
  const handleClick = url => {
    if (!blur) {
      if (GA.trackingEnabled) {
        GA.client.event({
          category: 'User',
          action: 'Contributor portfolio click',
          label: url
        });
      }
      window.open(url, '_blank');
    }
  };

  return (
    <Card className="Contributor" key={name} blur={blur} onClick={() => handleClick(portfolioUrl)}>
      <CardInnerWrapper>
        <ShareIcon type="external-link" />
        <ContributorAvatar src={avatarUrl} />
        <Name>{name}</Name>
        <StyledBio>{truncateBio(bio)}</StyledBio>
      </CardInnerWrapper>
    </Card>
  );
};

Contributor.defaultProps = {
  blur: false
};

Contributor.propTypes = {
  contributor: PropTypes.shape({
    name: PropTypes.string.isRequired,
    avatarUrl: PropTypes.string.isRequired,
    bio: PropTypes.string.isRequired,
    portfolioUrl: PropTypes.string.isRequired
  }).isRequired,
  blur: PropTypes.bool
};

export default Contributor;
